import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { type ColumnFiltersState, type PaginationState, type SortingState } from "@tanstack/react-table";
import { useAppSelector } from "../../../../redux/hooks";
import { apiClient } from "../../../../zodios/client";
import { clientToServerFiltering, clientToServerSorting } from "../../../../components/table/utils";
import { useDates } from "../../../../dates/DatesContext";
import { type ProductReportGrouping } from "../../../../zodios/common/productReportGroupingSchema";

type Params = {
  pagination: PaginationState;
  sorting: SortingState;
  filters?: ColumnFiltersState;
  search?: string;
};

export const useGetV4ProductReportByGrouping = (grouping: ProductReportGrouping) => {
  const marketPlace = useAppSelector(
    (state: { marketPlace: { marketPlace: string } }) => state.marketPlace.marketPlace,
  );

  const { start, end, preStart, preEnd } = useDates();
  return (params: Params) => {
    const cursor = params.pagination.pageIndex * params.pagination.pageSize || undefined;
    const search = params.search ? params.search.split(",") : [];

    const filters = params.filters;

    return queryOptions({
      queryKey: [
        {
          entity: "V4ProductReportByGrouping",
          marketPlace,
          grouping,
          params,
          dates: { start, end, preStart, preEnd },
        },
      ],
      queryFn: async () => {
        const queryParams = {
          queries: {
            grouping,
            marketplace: marketPlace,
            start_date: start.format("YYYY-MM-DD"),
            end_date: end.format("YYYY-MM-DD"),
            start_date_previous: preStart.format("YYYY-MM-DD"),
            end_date_previous: preEnd.format("YYYY-MM-DD"),
            filters: clientToServerFiltering(filters),
            cursor,
            limit: params.pagination.pageSize,
            ordering: clientToServerSorting(params.sorting),
            search: search,
          },
        };

        return await apiClient.getV4ProductReportByGrouping(queryParams);
      },
      placeholderData: keepPreviousData,
    });
  };
};

export type ProductGroupedQueryV4Row = Awaited<
  ReturnType<typeof apiClient.getV4ProductReportByGrouping>
>["data"][number];
